import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { MaskLines, EASE } from "@/components/site/motion";
import { OrderButton } from "@/components/site/OrderContext";

const FLAVORS = [
  { key: "berry", label: "Strawberry", bg: "#ff2e63", cup: "/img/cup-strawberry.webp", word: "STRAWBERRY", hue: 0, fx: [["/img/fx-strawberry.webp", "left-[2%] top-[10%] w-24 sm:w-32", 34, -18], ["/img/fx-strawberry.webp", "right-[0%] bottom-[22%] w-16 sm:w-24", 22, 24]] },
  { key: "mango", label: "Mango", bg: "#ff7a00", cup: "/img/cup-sunrise.webp", word: "MANGO", hue: 45, fx: [["/img/fx-mango.webp", "left-[4%] top-[14%] w-20 sm:w-28", 34, -12], ["/img/fx-lime.webp", "right-[2%] bottom-[24%] w-20 sm:w-28", 22, 20]] },
  { key: "matcha", label: "Matcha", bg: "#2e9e4f", cup: "/img/cup-matcha-mango.webp", word: "MATCHA", hue: 150, fx: [["/img/fx-leaf.webp", "left-[0%] top-[12%] w-24 sm:w-36", 34, -20], ["/img/fx-mango.webp", "right-[4%] bottom-[22%] w-16 sm:w-24", 22, 16]] },
];

const PEARLS = [["left-[18%] bottom-[10%]", 26, 14, "float-a"], ["right-[16%] top-[18%]", 18, 30, "float-b"], ["left-[8%] top-[48%]", 14, 24, "float-b"], ["right-[8%] top-[52%]", 22, 18, "float-a"], ["left-[46%] top-[4%]", 12, 40, "float-a"]];

const Parallax = ({ mx, my, depth, className, children }) => {
  const x = useTransform(mx, (v) => v * depth);
  const y = useTransform(my, (v) => v * depth);
  return <motion.div style={{ x, y }} exit={{ opacity: 0, scale: 0.4, transition: { duration: 0.35 } }} className={`pointer-events-none absolute ${className}`}>{children}</motion.div>;
};

const IntroSeal = () => {
  const [gone, setGone] = useState(false);
  if (gone) return null;
  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[120] grid place-items-center bg-[#1a0a10]"
      initial={{ clipPath: "circle(150% at 50% 50%)" }}
      animate={{ clipPath: "circle(0% at 50% 50%)" }}
      transition={{ duration: 0.75, ease: EASE, delay: 0.95 }}
      onAnimationComplete={() => setGone(true)}
    >
      <motion.div className="flex flex-col items-center gap-5" initial={{ scale: 0.6, opacity: 0, rotate: -30 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ duration: 0.7, ease: EASE }}>
        <img src="/img/logo.webp" alt="" className="h-28 w-28 rounded-full" />
        <span className="eyebrow text-[#ffd23f]">Boba and the Beat</span>
      </motion.div>
    </motion.div>
  );
};

const CupStage = ({ f, mx, my, D, first }) => {
  const rx = useSpring(useTransform(my, (v) => v * -10), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, (v) => v * 14), { stiffness: 80, damping: 15 });
  return (
    <div className="relative h-full w-full" style={{ perspective: 1000 }}>
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div className="aspect-square w-[78%] rounded-full bg-white/25 blur-3xl" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 1.2, delay: D, ease: EASE }} />
      </div>
      <div className="absolute inset-x-0 bottom-[2%] flex justify-center">
        <motion.img src="/img/fx-splash.webp" alt="" aria-hidden className="w-[120%] max-w-none" style={{ filter: `hue-rotate(${f.hue}deg) saturate(1.3)`, transition: "filter 0.9s" }} initial={{ scale: 0.15, opacity: 0, y: 40 }} animate={{ scale: 1, opacity: 0.95, y: 0 }} transition={{ duration: 1, delay: D + 0.3, ease: EASE }} />
      </div>
      <AnimatePresence initial>
        <motion.div key={f.key} className="absolute inset-0 flex items-end justify-center pb-[8%]" initial={{ opacity: 0, y: 140, rotate: 10 }} animate={{ opacity: 1, y: 0, rotate: 0 }} exit={{ opacity: 0, y: -90, rotate: -10 }} transition={{ duration: 1, ease: EASE, delay: first ? D : 0 }}>
          <motion.img src={f.cup} alt={`TeaDM ${f.label.toLowerCase()} drink in a branded cup`} data-testid="hero-cup" style={{ rotateX: rx, rotateY: ry }} className="h-[86%] w-auto max-w-[80%] object-contain drop-shadow-[0_45px_45px_rgba(26,10,16,0.45)]" />
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        {f.fx.map(([src, pos, depth, rot], i) => (
          <Parallax key={`${f.key}-${i}`} mx={mx} my={my} depth={depth} className={pos}>
            <div className={i % 2 ? "float-b" : "float-a"}>
              <motion.img src={src} alt="" aria-hidden className="w-full" initial={{ opacity: 0, scale: 0.2, rotate: rot - 60 }} animate={{ opacity: 1, scale: 1, rotate: rot }} exit={{ opacity: 0, scale: 0.3 }} transition={{ type: "spring", stiffness: 110, damping: 13, delay: (first ? D + 0.55 : 0.2) + i * 0.12 }} />
            </div>
          </Parallax>
        ))}
      </AnimatePresence>
      {PEARLS.map(([pos, size, depth, fl], i) => (
        <Parallax key={i} mx={mx} my={my} depth={depth} className={pos}>
          <div className={fl}>
            <motion.span className="pearl block" style={{ width: size, height: size }} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 160, damping: 12, delay: D + 0.6 + i * 0.07 }} />
          </div>
        </Parallax>
      ))}
    </div>
  );
};

const FlavorSwitch = ({ idx, setIdx, paused }) => (
  <div className="flex items-center gap-2" role="tablist" aria-label="Choose a flavor mood">
    {FLAVORS.map((f, i) => (
      <button key={f.key} type="button" role="tab" aria-selected={i === idx} onClick={() => setIdx(i)} data-testid={`hero-flavor-${f.key}`} className={`relative flex items-center gap-2 overflow-hidden rounded-full py-1.5 pl-1.5 pr-4 font-display text-xs font-bold uppercase tracking-wider transition-colors duration-300 ${i === idx ? "bg-[#fff5e8] text-[#1a0a10]" : "bg-[#1a0a10]/15 text-[#fff5e8] hover:bg-[#1a0a10]/25"}`}>
        <span className="grid h-8 w-8 place-items-center rounded-full" style={{ background: f.bg }}><img src={f.cup} alt="" className="h-7 w-auto" /></span>
        {f.label}
        {i === idx && !paused && <motion.span key={idx} className="absolute bottom-0 left-0 h-[3px] bg-[#1a0a10]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 6, ease: "linear" }} />}
      </button>
    ))}
  </div>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const [intro] = useState(() => !reduce && !sessionStorage.getItem("teadm-intro"));
  const [idx, setIdxRaw] = useState(0);
  const [switched, setSwitched] = useState(false);
  const [paused, setPaused] = useState(false);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const stageY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const D = intro ? 1.2 : 0.1;
  const f = FLAVORS[idx];

  const setIdx = (i) => {
    setSwitched(true);
    setIdxRaw(i);
  };

  useEffect(() => {
    if (intro) sessionStorage.setItem("teadm-intro", "1");
  }, [intro]);

  useEffect(() => {
    if (paused || reduce) return undefined;
    const t = setInterval(() => {
      setSwitched(true);
      setIdxRaw((i) => (i + 1) % FLAVORS.length);
    }, 6000);
    return () => clearInterval(t);
  }, [paused, reduce]);

  const onMove = (e) => {
    if (e.pointerType !== "mouse") return;
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <motion.section ref={ref} data-testid="hero" onPointerMove={onMove} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} className="grain relative min-h-[100svh] overflow-hidden text-[#fff5e8]" style={{ backgroundColor: FLAVORS[0].bg }} animate={{ backgroundColor: f.bg }} transition={{ duration: 0.9, ease: EASE }}>
      {intro && <IntroSeal />}
      <motion.div aria-hidden style={{ x: wordX }} className="pointer-events-none absolute -bottom-[3%] left-0 select-none whitespace-nowrap">
        <AnimatePresence initial={false}>
          <motion.span key={f.word} className="text-stroke block font-display text-[30vw] font-extrabold leading-none tracking-tighter text-[#fff5e8]/20 lg:text-[22vw]" initial={{ opacity: 0, x: 120 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -120, position: "absolute", top: 0 }} transition={{ duration: 0.9, ease: EASE }}>
            {f.word}
          </motion.span>
        </AnimatePresence>
      </motion.div>

      <div className="wrap relative z-10 grid min-h-[100svh] grid-cols-1 content-center gap-x-6 gap-y-4 pb-24 pt-28 lg:grid-cols-12 lg:pb-24 lg:pt-28">
        <div className="lg:col-span-7 lg:row-start-1 lg:self-end">
          <motion.p className="eyebrow mb-5 flex items-center gap-3 text-[#fff5e8]" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D, duration: 0.6 }}>
            <span className="pearl inline-block h-3 w-3" aria-hidden /> Boba and the Beat · Northern Virginia
          </motion.p>
          <MaskLines as="h1" onMount testId="hero-headline" lines={["Good vibes.", "Great sips."]} delay={D + 0.25} stagger={0.14} className="font-display text-[15vw] font-extrabold uppercase leading-[0.84] tracking-[-0.04em] sm:text-[12vw] lg:text-[8.6vw]" />
        </div>

        <motion.div style={{ y: stageY }} className="relative h-[46svh] sm:h-[56svh] lg:col-span-5 lg:col-start-8 lg:row-span-2 lg:row-start-1 lg:h-[70svh]">
          <CupStage f={f} mx={mx} my={my} D={D} first={!switched} />
        </motion.div>

        <motion.div className="lg:col-span-6 lg:row-start-2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 0.75, duration: 0.8, ease: EASE }}>
          <p className="max-w-xl text-base font-medium text-[#1a0a10] sm:text-lg">From handcrafted milk teas to Vietnamese coffee and refreshing fruit blends, discover your next favorite sip at TeaDM.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/menu" className="btn btn-ink" data-testid="hero-explore-menu-btn">Explore our menu <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/locations" className="btn btn-cream" data-testid="hero-find-teadm-btn">Find your TeaDM</Link>
            <OrderButton className="btn btn-ghost text-[#fff5e8]" testId="hero-order-online-btn">Order online</OrderButton>
          </div>
        </motion.div>
      </div>

      <motion.div className="wrap absolute inset-x-0 bottom-5 z-20 flex items-center justify-between gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1 }}>
        <div className="no-scrollbar -mx-1 overflow-x-auto px-1"><FlavorSwitch idx={idx} setIdx={setIdx} paused={paused} /></div>
        <span className="eyebrow hidden items-center gap-2 text-[#fff5e8] sm:flex">Scroll <ArrowDown className="h-4 w-4 animate-bounce" /></span>
      </motion.div>
    </motion.section>
  );
};
